import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Editor as TipTapEditor } from '@tiptap/react'
import debounce from 'lodash.debounce'

import {
  Label,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { Editor } from '@/components/editor/Editor'
import { useDocumentState } from '@/state'
import { useEditDocumentPageLabel } from '@/services/queries/document_label/useEditDocumentPageLabel'
import { cn } from '@/utils'
import { Spinner } from '@/components/ui/spinner'
import { ExtensionsEnum } from '@/components/editor/constants'
import { useAnnotationLayer } from './Annotations/queries'
import { getAbsoluteYFromSubPage } from './Annotations/utils'
import { DocumentPage } from '@/lib/api/client'
import { useAnnotationsContext } from './Annotations/AnnotationsContext'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useParams } from 'wouter'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { useIsTemplateAttributeMultiSelected } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useIsTemplateAttributeMultiSelected'
import { useMultiSelectHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectHandler'
import { isMultiSelectModifierPressed } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/isMultiSelectModifierPressed'
import { ElementResizeOverlay } from './ElementResizeOverlay'

interface OperationLabelProps {
  template: TemplateAttribute<Label>
  isReadOnly: boolean
  documentPage: DocumentPage
  dataType: 'label'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}

export const OperationLabel = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: OperationLabelProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])

  const editorRef = useRef<TipTapEditor | null>(null)
  const labelContainerRef = useRef<HTMLDivElement>(null)
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()

  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
    maxZIndex,
  } = useAnnotationLayer(documentPage.id as string)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )
  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplateElement({
      documentPageId,
    })
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const getDocumentState = useDocumentState((state) => state.getState)
  const selectLabelElement = useCallback(
    (isRemovable = true) => {
      setSelectedElement({
        documentPageId,
        id: template?.id as string,
        isRemovable,
        type: dataType,
      })
    },
    [dataType, documentPageId, setSelectedElement, template?.id],
  )
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === template.id,
    [selectedElement, documentPageId, template.id],
  )
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template, documentPageId],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const size = useMemo(() => template?.template_values?.size, [template])
  const { setIsResizing } = useAnnotationsContext()
  const backgroundColor = useMemo(
    () => template?.template_values?.backgroundColor ?? 'transparent',
    [template],
  )
  const updateBackgroundColorHandler = useCallback(
    (color: string) => {
      if (template.id) {
        updateTemplate({
          payload: {
            backgroundColor: color === backgroundColor ? 'transparent' : color,
          },
          data_type: dataType,
          templateAttributeId: template.id,
        }).catch((error) => console.error(error))
      }
    },
    [dataType, template.id, updateTemplate, backgroundColor],
  )

  const updateLabelSizeHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position: { ...position, z: positionData?.z },
        },
        data_type: dataType,
        templateAttributeId: template.id as string,
      })
    },
    [dataType, template.id, positionData, updateTemplate],
  )

  const [containerDimensions, setContainerDimensions] = useState({
    width: 154,
    height: 25,
  })

  useEffect(() => {
    if (size) {
      setContainerDimensions(size)
    }
  }, [size])

  const [{ position, delta }, setPosition] = useState({
    position: positionData,
    delta: INITIAL_DELTA,
  })

  const { mutate: updateLabel } = useEditDocumentPageLabel({
    labelTemplate: template,
    documentPageId,
  })

  const containerId = useMemo(
    () => `${documentPageId}-${template.id}`,
    [documentPageId, template.id],
  )

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useMultiSelectDraggable({
      disabled: isAddingElement || isReadOnly || !!croppingImageId,
      id: template.id as string,
      data: { type: dataType },
    })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: isSelected && Boolean(selectedElement?.isRemovable),
    showGridOnNudge: true,
    onNudgeStart,
    onNudgeEnd,
    template,
    documentPage,
  })

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  const saveLabel = useCallback(
    debounce(({ editor }: { editor: TipTapEditor }) => {
      updateLabel({
        editor,
      })
    }, 500),
    [updateLabel],
  )

  const mutatedListeners = useMemo(
    () => ({
      ...listeners,
      onMouseDown: (e: React.MouseEvent<Element, MouseEvent>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
      onTouchStart: (e: React.TouchEvent<Element>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
    }),
    [listeners, containerId],
  )

  const isLoading = useMemo(() => isUpdatingTemplate, [isUpdatingTemplate])
  const content = useMemo(() => template?.template_values?.content || '', [])

  const isMultiSelected = useIsTemplateAttributeMultiSelected({
    templateAttributeId: template.id,
  })
  const { registerSelectableElementRef } = useRelativeToAbsolutePositioning()

  const {
    onPointerDown: multiSelectKeydownHandler,
    onPointerUp: multiSelectKeyupHandler,
  } = useMultiSelectHandler()

  const isSelectKeyPressed = useDocumentState(
    (state) => state.isSelectKeyPressed,
  )
  const [isEditorFocused, setIsEditorFocused] = useState(false)

  const nodeRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el)
      if (template.id) {
        registerSelectableElementRef(template.id, el)
      }
    },
    [template.id, setNodeRef, registerSelectableElementRef],
  )

  const optionsGroups = useMemo(
    () => [
      {
        options: [
          {
            label: 'Bring to front',
            action: () => bringToFront(template?.id as string),
          },
          {
            label: 'Bring forward',
            action: () => bringForward(template?.id as string),
          },
          {
            label: 'Send backward',
            action: () => sendBackward(template?.id as string),
          },
          {
            label: 'Send to back',
            action: () => sendToBack(template?.id as string),
          },
        ],
      },
    ],
    [bringToFront, bringForward, sendBackward, sendToBack, template?.id],
  )

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      onAuxClick={() => {
        selectLabelElement()
      }}
      optionsGroups={optionsGroups}
      zIndex={maxZIndex + 3}
    >
      <div
        id={containerId}
        ref={nodeRefCallback}
        className={cn('flex rounded-md box-content absolute', {
          'cursor-move': !isReadOnly,
          'cursor-default': isReadOnly,
          'rounded-md': isSelected || isMultiSelected,
          'shadow-md': isDragging,
          'shadow-gray-200': isDragging,
          'bg-sky-200/10': isDragging,
          'z-[100]': isDragging,
        })}
        {...bind}
        {...attributes}
        {...mutatedListeners}
        style={{
          translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
          top: getAbsoluteYFromSubPage({
            subPageId,
            relativeY: position?.y || 0,
            subPageIds: documentSubPages,
          }),
          left: position?.x || 0,
          zIndex: positionData?.z ?? 1000,
          padding: '4px', // Match the border width that was removed
        }}
        onClick={(e) => e.stopPropagation()}
        onClickCapture={(e) => {
          if (
            isMultiSelectModifierPressed(
              e,
              getDocumentState().isSelectKeyPressed,
            )
          ) {
            return
          }
          if (e.target !== e.currentTarget) return
          selectLabelElement()
        }}
        onPointerDownCapture={(e) => {
          const isSelectKeyPressed = isMultiSelectModifierPressed(
            e,
            getDocumentState().isSelectKeyPressed,
          )
          if (!isSelectKeyPressed) return
          multiSelectKeydownHandler(e as any, template)
        }}
        onPointerUpCapture={(e) => {
          const isSelectKeyPressed = isMultiSelectModifierPressed(
            e,
            getDocumentState().isSelectKeyPressed,
          )
          if (!isSelectKeyPressed) return
          multiSelectKeyupHandler(e)
        }}
      >
        <div
          ref={labelContainerRef}
          style={{
            width:
              containerDimensions.width > 0 && containerDimensions.height > 0
                ? containerDimensions.width
                : 154,
            height:
              containerDimensions.width > 0 && containerDimensions.height > 0
                ? containerDimensions.height
                : 25,
            maxWidth: 420,
            maxHeight: 740,
          }}
        >
          <>
            {isLoading && (
              <div className="absolute h-full w-full bg-zinc-400/60 z-10">
                <Spinner />
              </div>
            )}
            <Editor
              extensionsList={[
                ExtensionsEnum.COLOR,
                ExtensionsEnum.ICONS,
                ExtensionsEnum.HIGHLIGHT,
                ExtensionsEnum.MENTION,
                ExtensionsEnum.UNDERLINE,
                ExtensionsEnum.BACKGROUND,
                ExtensionsEnum.SLASH_COMMAND,
              ]}
              projectId={projectId}
              documentId={documentId}
              documentPageId={documentPageId}
              className={cn(
                'flex-1 py-0 px-1 document-label min-h-0 text-xs bg-transparent',
              )}
              onFocus={() => {
                setIsEditorFocused(true)
              }}
              onBlur={({ editor }) => {
                setIsEditorFocused(false)
                saveLabel({ editor })
              }}
              onClick={() => {
                selectLabelElement(false)
              }}
              onUpdate={saveLabel}
              onPointerDown={(e) => {
                multiSelectKeydownHandler(e as any, template)
              }}
              onPointerUp={(e) => {
                multiSelectKeyupHandler(e)
              }}
              content={content}
              disabled={!isEditorFocused && (isReadOnly || isSelectKeyPressed)}
              menuOffsetY={-20}
              placeholder="Write something..."
              isDragging={isDragging}
              updateBomWithSlashCommands={true}
              editorRef={editorRef}
              isTransparent
              backgroundColor={backgroundColor}
              updateBackgroundColorHandler={updateBackgroundColorHandler}
              dropdownZIndex={maxZIndex + 3}
            />
          </>
        </div>

        {/* Portal-based resize handlers that render on top */}
        <ElementResizeOverlay
          isVisible={
            !isReadOnly && (isSelected || isMultiSelected) && !isDragging
          }
          containerRef={labelContainerRef}
          maxZIndex={maxZIndex}
          currentBoundingWidth={containerDimensions.width}
          currentBoundingHeight={containerDimensions.height}
          zoom={zoom}
          showOutline={isSelected || isMultiSelected}
          outlineClassName="border-4 border-indigo-400/50 rounded-md"
          outlineBorderWidth={4}
          lockAspectRatio={false}
          minWidth={154}
          minHeight={25}
          enabledHandlers={{
            topLeft: true,
            topRight: true,
            bottomLeft: true,
            bottomRight: true,
          }}
          onResizeStart={() => {
            if (setIsResizing) setIsResizing(true)
          }}
          onResize={(newBoundingWidth, newBoundingHeight, direction) => {
            const widthDiff = newBoundingWidth - containerDimensions.width
            const heightDiff = newBoundingHeight - containerDimensions.height

            const deltaUpdates = {
              topLeft: { x: -widthDiff, y: -heightDiff },
              bottomLeft: { x: -widthDiff, y: 0 },
              topRight: { x: 0, y: -heightDiff },
              bottomRight: { x: 0, y: 0 },
              top: { x: 0, y: -heightDiff },
              bottom: { x: 0, y: 0 },
              left: { x: -widthDiff, y: 0 },
              right: { x: 0, y: 0 },
            }

            // Enforce max constraints
            const clampedWidth = Math.min(newBoundingWidth, 420)
            const clampedHeight = Math.min(newBoundingHeight, 740)

            setContainerDimensions({
              width: clampedWidth,
              height: clampedHeight,
            })

            setPosition((prevPosition) => ({
              ...prevPosition,
              delta: {
                x: prevPosition.delta.x + (deltaUpdates[direction]?.x || 0),
                y: prevPosition.delta.y + (deltaUpdates[direction]?.y || 0),
              },
            }))
          }}
          onResizeStop={(finalWidth, finalHeight) => {
            if (setIsResizing) setIsResizing(false)

            if (finalWidth < 154 || finalHeight < 25) return

            updateLabelSizeHandler(
              {
                width: finalWidth,
                height: finalHeight,
              },
              {
                x: (position?.x || 0) + delta.x,
                y: (position?.y || 0) + delta.y,
              },
            )
              .then(() => {
                selectLabelElement()
              })
              .catch((error) => {
                console.error('Error updating label size:', error)
              })
          }}
        />
      </div>
    </OptionsContextMenu>
  )
}
